import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Zap, Loader2, Crown, Type, Image as ImageIcon, Sparkles, Wand2, LogIn, Lock } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { ImagePreview } from "@/components/ImagePreview";
import { GenerationHistory } from "@/components/GenerationHistory";
import { ImageUpload } from "@/components/ImageUpload";
import { SEO } from "@/components/SEO";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";

const ASPECT_RATIOS = [
  { value: "1:1", label: "Carré (1:1)", ratio: 1 },
  { value: "16:9", label: "Paysage (16:9)", ratio: 16/9 },
  { value: "9:16", label: "Portrait (9:16)", ratio: 9/16 },
  { value: "4:3", label: "Standard (4:3)", ratio: 4/3 },
  { value: "3:4", label: "Portrait (3:4)", ratio: 3/4 },
  { value: "21:9", label: "Cinéma (21:9)", ratio: 21/9 },
  { value: "3:2", label: "Photo (3:2)", ratio: 3/2 },
  { value: "2:3", label: "Portrait Photo (2:3)", ratio: 2/3 },
  { value: "4:5", label: "Instagram (4:5)", ratio: 4/5 },
];

// Resolutions for Floop Pro only
const PRO_RESOLUTIONS = [
  { value: "standard", label: "Standard (1K)", baseSize: 1024 },
  { value: "fullhd", label: "Full HD (2K)", baseSize: 1920 },
  { value: "cinema4k", label: "Cinema 4K", baseSize: 3840 },
];

const NUMBER_OPTIONS = [1, 2, 3, 4];

const Create = () => {
  const [strength, setStrength] = useState([75]);
  const [prompt, setPrompt] = useState("");
  const [sourceImageUrl, setSourceImageUrl] = useState("");
  const [model, setModel] = useState<"floop" | "floop-pro">("floop");
  const [aspectRatio, setAspectRatio] = useState("1:1");
  const [resolution, setResolution] = useState("standard");
  const [numberOfImages, setNumberOfImages] = useState(1);
  const [generatedImages, setGeneratedImages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const promptRef = useRef<HTMLTextAreaElement>(null);
  const { plan, isPro } = useSubscription();
  const [activeTab, setActiveTab] = useState("text-to-image");

  // Check if user can use image-to-image (Pro/Business only)
  const canUseImageToImage = isPro;

  // Reset resolution when switching away from Floop Pro
  useEffect(() => {
    if (model === "floop") {
      setResolution("standard");
    }
  }, [model]);

  const selectedAspectRatio = ASPECT_RATIOS.find(r => r.value === aspectRatio) || ASPECT_RATIOS[0];
  const selectedResolution = model === "floop-pro" 
    ? (PRO_RESOLUTIONS.find(r => r.value === resolution) || PRO_RESOLUTIONS[0])
    : { value: "standard", label: "Standard (1K)", baseSize: 1024 };
  
  // Calculate actual dimensions based on aspect ratio and resolution
  const getImageDimensions = () => {
    const baseSize = selectedResolution.baseSize;
    const ratio = selectedAspectRatio.ratio;
    
    if (ratio >= 1) {
      return { width: baseSize, height: Math.round(baseSize / ratio) };
    } else {
      return { width: Math.round(baseSize * ratio), height: baseSize };
    }
  };
  
  const dimensions = getImageDimensions();
  
  // Calculate credits based on model and resolution
  const getCredits = () => {
    if (model === "floop") {
      return 1; // Floop is always 1 credit (fixed 1K resolution)
    }
    // Floop Pro pricing
    switch (resolution) {
      case "standard": return 5;
      case "fullhd": return 8;
      case "cinema4k": return 15;
      default: return 5;
    }
  };
  
  const baseCredits = getCredits();
  const totalCredits = baseCredits * numberOfImages;

  const handleEnhancePrompt = async () => {
    if (!prompt.trim()) {
      toast({
        title: "Prompt requis",
        description: "Entrez d'abord une description à améliorer.",
        variant: "destructive",
      });
      return;
    }

    setIsEnhancing(true);
    try {
      const { data, error } = await supabase.functions.invoke("enhance-prompt", {
        body: { prompt, language: "fr" }
      });

      if (error) throw error;

      if (data?.enhancedPrompt) {
        setPrompt(data.enhancedPrompt);
        toast({
          title: "Prompt amélioré ✨",
          description: "Votre description a été enrichie par l'IA.",
        });
      }
    } catch (error) {
      console.error("Enhance error:", error);
      toast({
        title: "Erreur",
        description: "Impossible d'améliorer le prompt. Réessayez.",
        variant: "destructive",
      });
    } finally {
      setIsEnhancing(false);
    }
  };

  // Check if this is user's first generation (for upgrade popup)
  const checkFirstGeneration = useCallback(async () => {
    if (!user) return false;
    try {
      const { count } = await supabase
        .from("generations")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id);
      return count === 1; // Just generated their first image
    } catch {
      return false;
    }
  }, [user]);

  const handleGenerate = async (mode: "text-to-image" | "image-to-image") => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      setShowAuthDialog(true);
      return;
    }

    if (!prompt.trim()) {
      toast({
        title: "Prompt requis",
        description: "Veuillez entrer une description.",
        variant: "destructive",
      });
      return;
    }

    if (mode === "image-to-image" && !sourceImageUrl.trim()) {
      toast({
        title: "Image source requise",
        description: "Veuillez sélectionner ou entrer une image source.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    setGeneratedImages([]);

    try {
      // Map floop model names to edge function names (floot internally)
      const functionName = model === "floop" ? "floot" : "floot-pro";
      
      // Generate multiple images in parallel
      const generationPromises = Array.from({ length: numberOfImages }, async () => {
        const body = model === "floop" 
          ? { prompt, width: dimensions.width, height: dimensions.height, mode }
          : { 
              prompt, 
              image_url: mode === "image-to-image" ? sourceImageUrl : undefined,
              strength: strength[0] / 100,
              width: dimensions.width, 
              height: dimensions.height,
              mode
            };

        const { data, error } = await supabase.functions.invoke(functionName, { body });
        if (error) throw error;
        return data?.image_url;
      });

      const results = await Promise.allSettled(generationPromises);
      const successfulImages = results
        .filter((result): result is PromiseFulfilledResult<string> => 
          result.status === "fulfilled" && !!result.value
        )
        .map(result => result.value);

      if (successfulImages.length > 0) {
        setGeneratedImages(successfulImages);
        queryClient.invalidateQueries({ queryKey: ["generations"] });
        toast({
          title: `${successfulImages.length} image${successfulImages.length > 1 ? "s" : ""} générée${successfulImages.length > 1 ? "s" : ""} ✨`,
          description: "Vos créations sont prêtes !",
        });

        // Check if first generation and show upgrade popup (only for non-pro)
        if (model === "floop") {
          const isFirst = await checkFirstGeneration();
          if (isFirst) {
            setTimeout(() => setShowUpgradeDialog(true), 1500);
          }
        }
      } else {
        throw new Error("Aucune image générée");
      }

      // Check for failed generations
      const failedCount = results.filter(r => r.status === "rejected").length;
      if (failedCount > 0) {
        toast({
          title: "Attention",
          description: `${failedCount} image${failedCount > 1 ? "s n'ont" : " n'a"} pas pu être générée${failedCount > 1 ? "s" : ""}.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast({
        title: "Oops !",
        description: "Un problème est survenu. Réessayez !",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Stable prompt change handler to prevent mobile input issues
  const handlePromptChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompt(e.target.value);
  }, []);

  // Prompt section component rendered inline to prevent focus loss on mobile
  const renderPromptSection = (placeholder: string, isTransformation: boolean) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {isTransformation ? "Ta transformation magique" : "Décris ta vision"}
        </label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleEnhancePrompt}
          disabled={isEnhancing || isLoading || !prompt.trim()}
          className="text-xs text-primary hover:text-primary/80 hover:bg-primary/10 h-7 px-2"
        >
          {isEnhancing ? (
            <>
              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              Amélioration...
            </>
          ) : (
            <>
              <Wand2 className="w-3 h-3 mr-1" />
              Améliorer avec l'IA
            </>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <AppLayout title="Créer">
      <SEO 
        title="Créer une image"
        description="Générez des images uniques avec l'IA Floop. Transformez vos idées en visuels époustouflants en quelques secondes."
        keywords="créer image IA, génération image, text to image, Floop créer"
      />
      <div className="p-6">
        <div className="grid lg:grid-cols-[420px,1fr] gap-6">
          {/* Left Panel - Form */}
          <div className="space-y-5">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-secondary rounded-xl">
                <TabsTrigger value="text-to-image" className="data-[state=active]:bg-gradient-primary data-[state=active]:text-white rounded-lg">
                  <Type className="w-4 h-4 mr-2" />
                  Texte → Image
                </TabsTrigger>
                <TabsTrigger 
                  value="image-to-image" 
                  className="data-[state=active]:bg-gradient-primary data-[state=active]:text-white rounded-lg relative"
                  disabled={!canUseImageToImage}
                >
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Image → Magie
                  {!canUseImageToImage && (
                    <Lock className="w-3 h-3 ml-1 text-muted-foreground" />
                  )}
                </TabsTrigger>
              </TabsList>
              
              {/* Pro required banner for image-to-image */}
              {!canUseImageToImage && activeTab === "text-to-image" && (
                <div className="mt-3 p-3 bg-highlight/10 border border-highlight/20 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-highlight/20 flex items-center justify-center flex-shrink-0">
                      <Crown className="w-5 h-5 text-highlight" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">Image → Magie réservé aux membres Pro</p>
                      <p className="text-xs text-muted-foreground">Passez au plan Pro pour transformer vos images avec l'IA</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate("/subscription")}
                      className="bg-highlight hover:bg-highlight/90 text-highlight-foreground text-xs"
                    >
                      Passer Pro
                    </Button>
                  </div>
                </div>
              )}
              
              {/* Model Selector */}
              <div className="mt-5 space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Modèle IA</label>
                <Select value={model} onValueChange={(value: "floop" | "floop-pro") => setModel(value)}>
                  <SelectTrigger className="w-full bg-secondary border-border rounded-xl h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border rounded-xl">
                    <SelectItem value="floop" className="rounded-lg">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        <span>Floop</span>
                        <span className="text-xs text-muted-foreground ml-2">Rapide & fun</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="floop-pro" className="rounded-lg">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-highlight" />
                        <span>Floop Pro</span>
                        <span className="text-xs text-muted-foreground ml-2">Haute qualité</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Aspect Ratio & Resolution Row */}
              <div className={`mt-4 grid gap-3 ${model === "floop-pro" ? "grid-cols-2" : "grid-cols-1"}`}>
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Format</label>
                  <Select value={aspectRatio} onValueChange={setAspectRatio}>
                    <SelectTrigger className="w-full bg-secondary border-border rounded-xl h-12">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border rounded-xl max-h-[280px]">
                      {ASPECT_RATIOS.map((ar) => (
                        <SelectItem key={ar.value} value={ar.value} className="rounded-lg">
                          {ar.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Resolution selector - Floop Pro only */}
                {model === "floop-pro" && (
                  <div className="space-y-2">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Résolution</label>
                    <Select value={resolution} onValueChange={setResolution}>
                      <SelectTrigger className="w-full bg-secondary border-border rounded-xl h-12">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border rounded-xl">
                        {PRO_RESOLUTIONS.map((res) => (
                          <SelectItem key={res.value} value={res.value} className="rounded-lg">
                            <div className="flex items-center gap-2">
                              <span>{res.label}</span>
                              {res.value === "cinema4k" && (
                                <span className="text-xs text-highlight">Premium</span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Number of Images */}
              <div className="mt-4 space-y-2">
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Nombre d'images</label>
                <div className="flex gap-2">
                  {NUMBER_OPTIONS.map((num) => (
                    <button
                      key={num}
                      onClick={() => setNumberOfImages(num)}
                      className={`flex-1 h-12 rounded-xl font-semibold transition-all duration-200 ${
                        numberOfImages === num
                          ? "bg-secondary border-2 border-primary text-foreground"
                          : "bg-secondary/50 border border-border text-muted-foreground hover:bg-secondary hover:text-foreground"
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>

              <TabsContent value="text-to-image" className="space-y-5 mt-5">
                {renderPromptSection("Un chat astronaute sur la lune avec des étoiles colorées...", false)}
                <Textarea
                  value={prompt}
                  onChange={handlePromptChange}
                  placeholder="Un chat astronaute sur la lune avec des étoiles colorées..."
                  className="min-h-[120px] bg-secondary border-border resize-none text-foreground placeholder:text-muted-foreground rounded-xl"
                  disabled={isLoading || isEnhancing}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                />
                
                <Button 
                  onClick={() => handleGenerate("text-to-image")}
                  disabled={isLoading || isEnhancing}
                  className="w-full bg-highlight hover:bg-highlight/90 text-highlight-foreground font-bold py-6 rounded-xl shadow-lg transition-all duration-200 hover:scale-[1.02]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Génération en cours...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" fill="currentColor" />
                      Lancer Floop{model === "floop-pro" ? " Pro" : ""} ({totalCredits} Crédit{totalCredits > 1 ? "s" : ""})
                    </>
                  )}
                </Button>
              </TabsContent>
              
              <TabsContent value="image-to-image" className="space-y-5 mt-5">
                <ImageUpload
                  value={sourceImageUrl}
                  onChange={setSourceImageUrl}
                  disabled={isLoading}
                />
                
                {renderPromptSection("Transforme en peinture style Van Gogh...", true)}
                <Textarea
                  value={prompt}
                  onChange={handlePromptChange}
                  placeholder="Transforme en peinture style Van Gogh..."
                  className="min-h-[120px] bg-secondary border-border resize-none text-foreground placeholder:text-muted-foreground rounded-xl"
                  disabled={isLoading || isEnhancing}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-gramm="false"
                  data-gramm_editor="false"
                  data-enable-grammarly="false"
                />
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Force de transformation</label>
                    <span className="text-sm font-semibold text-primary">{strength[0]} %</span>
                  </div>
                  <Slider
                    value={strength}
                    onValueChange={setStrength}
                    max={100}
                    step={1}
                    disabled={isLoading}
                    className="py-2"
                  />
                </div>
                
                <Button
                  onClick={() => handleGenerate("image-to-image")}
                  disabled={isLoading || isEnhancing}
                  className="w-full bg-highlight hover:bg-highlight/90 text-highlight-foreground font-bold py-6 rounded-xl shadow-lg transition-all duration-200 hover:scale-[1.02]"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Transformation en cours...
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" fill="currentColor" />
                      Lancer Floop{model === "floop-pro" ? " Pro" : ""} ({totalCredits} Crédit{totalCredits > 1 ? "s" : ""})
                    </>
                  )}
                </Button>
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Right Panel - Preview */}
          <div className="bg-card border border-border rounded-2xl p-6 flex items-center justify-center min-h-[500px]">
            <ImagePreview 
              imageUrls={generatedImages} 
              isLoading={isLoading} 
              aspectRatio={selectedAspectRatio.ratio}
              loadingCount={numberOfImages}
            />
          </div>
        </div>

        {/* History Section */}
        <div className="mt-10">
          <h2 className="text-xl font-bold text-foreground mb-4">Historique des créations</h2>
          <GenerationHistory 
            onSelect={(gen) => setGeneratedImages([gen.image_url])}
            onRemix={(gen) => {
              setPrompt(gen.prompt);
              // If it was an image-to-image generation, also set the source
              if (gen.source_image_url) {
                setSourceImageUrl(gen.source_image_url);
              }
              // Scroll to top of the form
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          />
        </div>
      </div>

      {/* Auth Required Dialog */}
      <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
        <DialogContent className="bg-card border-border rounded-2xl max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <LogIn className="w-8 h-8 text-primary" />
            </div>
            <DialogTitle className="text-xl text-center">Connexion requise</DialogTitle>
            <DialogDescription className="text-center text-muted-foreground">
              Pour générer des images avec Floop, vous devez d'abord créer un compte ou vous connecter.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-4">
            <Button 
              onClick={() => navigate("/auth")}
              className="w-full bg-gradient-primary text-white font-semibold rounded-xl"
            >
              Créer un compte gratuit
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate("/auth")}
              className="w-full rounded-xl"
            >
              J'ai déjà un compte
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upgrade to Pro Dialog */}
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="bg-card border-border rounded-2xl max-w-md">
          <DialogHeader>
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-highlight/10 flex items-center justify-center">
              <Crown className="w-8 h-8 text-highlight" />
            </div>
            <DialogTitle className="text-xl text-center">🎉 Première image générée !</DialogTitle>
            <DialogDescription className="text-center text-muted-foreground">
              Félicitations ! Passez à <span className="font-semibold text-highlight">Floop Pro</span> pour des images en haute qualité et des résolutions jusqu'à 4K.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-secondary/50 rounded-xl p-4 my-4">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-highlight" />
                <span>Résolutions HD, 2K et 4K</span>
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-highlight" />
                <span>Qualité studio professionnelle</span>
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-highlight" />
                <span>Retouche IA avancée</span>
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-3">
            <Button 
              onClick={() => {
                setShowUpgradeDialog(false);
                navigate("/subscription");
              }}
              className="w-full bg-highlight hover:bg-highlight/90 text-highlight-foreground font-semibold rounded-xl"
            >
              <Crown className="w-4 h-4 mr-2" />
              Découvrir les offres Pro
            </Button>
            <Button 
              variant="ghost" 
              onClick={() => setShowUpgradeDialog(false)}
              className="w-full text-muted-foreground"
            >
              Plus tard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default Create;